import { Bug, ClipboardCheck, GitBranch, Zap } from "lucide-react";

const bugData = [
  { month: "Jan", found: 18, escaped: 3 },
  { month: "Feb", found: 24, escaped: 2 },
  { month: "Mar", found: 15, escaped: 1 },
  { month: "Apr", found: 31, escaped: 0 },
  { month: "May", found: 22, escaped: 1 },
  { month: "Jun", found: 19, escaped: 0 },
];

const metrics = [
  { icon: Bug, label: "Automation Stack", value: "Selenium", sub: "WebDriver with Java", color: "var(--primary)" },
  { icon: GitBranch, label: "CI Integration", value: "Jenkins", sub: "quality checks in delivery flow", color: "var(--accent)" },
  { icon: Zap, label: "Modern E2E", value: "Playwright", sub: "plus Cypress where useful", color: "var(--primary)" },
  { icon: ClipboardCheck, label: "Testing Focus", value: "Shift-left", sub: "clear criteria and early feedback", color: "var(--accent)" },
];

const strategies = [
  { title: "Risk-Based Testing", desc: "Prioritize test coverage by feature risk score, with deeper checks around high-impact and high-change areas." },
  { title: "Shift-Left QA", desc: "Bring test planning into grooming so acceptance criteria are clear, measurable, and testable from the start." },
  { title: "Automation Discipline", desc: "Keep suites focused on meaningful coverage, fast feedback, and maintainable regression paths." },
  { title: "Flake Tracking", desc: "Treat unstable tests as product work, isolate root causes, and keep CI signals useful for the team." },
];

export default function QAWork() {
  const max = Math.max(...bugData.flatMap((item) => [item.found, item.escaped]));
  return (
    <section id="qa-work" className="border-t px-4 py-16 sm:px-6 sm:py-20 lg:py-28" style={{ background: "var(--card)", borderColor: "var(--border)" }}>
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 sm:mb-12 section-label">// 04 &nbsp; QA WORK</div>
        <h2 className="mb-4 section-heading">Quality engineering practice</h2>
        <p className="mb-10 max-w-xl body-copy sm:mb-14">I build automation that supports delivery instead of slowing it down: clear coverage, reliable feedback, and test planning that starts before implementation is finished.</p>

        <div className="mb-10 grid gap-3 sm:mb-12 sm:grid-cols-2 lg:grid-cols-4">
          {metrics.map(({ icon: Icon, label, value, sub, color }) => (
            <div key={label} className="rounded border p-4 sm:p-5" style={{ background: "var(--background)", borderColor: "var(--border)" }}>
              <Icon size={14} style={{ color, marginBottom: "0.75rem" }} />
              <div style={{ fontFamily: "var(--font-display)", fontSize: "clamp(1.25rem, 7vw, 1.75rem)", fontWeight: 600, color, lineHeight: 1, marginBottom: "0.25rem" }}>{value}</div>
              <div className="card-title" style={{ fontSize: "0.75rem" }}>{label}</div>
              <div className="mono-small">{sub}</div>
            </div>
          ))}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded border p-4 sm:p-6" style={{ background: "var(--background)", borderColor: "var(--border)" }}>
            <div className="mb-6 card-title">Example test signal tracked over a release cycle</div>
            <div className="mb-5 flex flex-wrap items-center gap-3 sm:gap-5">
              <span className="chart-key" style={{ "--key-color": "var(--primary)" }}>Caught pre-prod</span>
              <span className="chart-key" style={{ "--key-color": "var(--destructive)" }}>Escaped</span>
            </div>
            <div className="chart-grid">
              {bugData.map((item) => (
                <div key={item.month} className="chart-month">
                  <div className="chart-bars">
                    <span className="chart-bar" style={{ height: (item.found / max) * 100 + "%", background: "var(--primary)" }} />
                    <span className="chart-bar" style={{ height: (item.escaped / max) * 100 + "%", background: "var(--destructive)" }} />
                  </div>
                  <span className="mono-small">{item.month}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {strategies.map(({ title, desc }) => (
              <div key={title} className="rounded border p-4 sm:p-5" style={{ background: "var(--background)", borderColor: "var(--border)" }}>
                <div className="mb-1 card-title">{title}</div>
                <p className="body-small leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
